// Browser-local video storage hook.
import { useState, useEffect, useCallback } from 'react'
import type { Video } from '@/lib/local-types'
import {
  StoredVideo,
  getStoredVideos,
  storeVideoWithFile,
  getVideoById,
  deleteVideo as deleteStoredVideo,
  duplicateStoredVideo,
} from '@/features/videos/videos-store'
import { LOCAL_LIBRARY_ID } from '@/lib/browser-persistence'

interface LocalVideosState {
  videos: Video[]
  isLoading: boolean
  error: string | null
  storageInfo: {
    used: number
    quota: number
    videoCount: number
    percentUsed: number
  }
}

interface UploadProgress {
  videoId: string
  progress: number
  status: 'uploading' | 'processing' | 'complete' | 'error'
  error?: string
}

const DEFAULT_LOCAL_QUOTA = 1024 * 1024 * 1024

function storedToVideo(video: StoredVideo): Video {
  return {
    id: video.id,
    userId: LOCAL_LIBRARY_ID,
    name: video.name,
    blobUrl: video.url,
    url: video.url,
    thumbnailUrl: video.thumbnail,
    mimeType: video.mimeType || video.format?.mimeType || 'video/mp4',
    size: video.size,
    duration: video.duration,
    frameCount: video.frameCount,
    uploadDate: video.uploadDate,
  }
}

function calculateStorageInfo(videos: Video[]) {
  const used = videos.reduce((sum, video) => sum + video.size, 0)
  const percentUsed = DEFAULT_LOCAL_QUOTA > 0 ? Math.min(100, Math.round((used / DEFAULT_LOCAL_QUOTA) * 100)) : 0

  return {
    used,
    quota: DEFAULT_LOCAL_QUOTA,
    videoCount: videos.length,
    percentUsed,
  }
}

export function useCloudVideos() {
  const [state, setState] = useState<LocalVideosState>({
    videos: [],
    isLoading: true,
    error: null,
    storageInfo: { used: 0, quota: DEFAULT_LOCAL_QUOTA, videoCount: 0, percentUsed: 0 }
  })
  const [uploadProgress, setUploadProgress] = useState<Map<string, UploadProgress>>(new Map())

  const fetchVideos = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
    }

    try {
      const videos = (await getStoredVideos()).map(storedToVideo)
      setState({
        videos,
        isLoading: false,
        error: null,
        storageInfo: calculateStorageInfo(videos),
      })
    } catch (error) {
      console.error('Failed to load local videos:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load videos'
      }))
    }
  }, [])

  useEffect(() => {
    fetchVideos(true)
  }, [fetchVideos])

  const uploadVideo = useCallback(async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Video | null> => {
    const tempId = crypto.randomUUID()

    try {
      setUploadProgress(prev => new Map(prev).set(tempId, {
        videoId: tempId,
        progress: 20,
        status: 'uploading'
      }))
      onProgress?.(20)

      const storedVideo = await storeVideoWithFile(file, {
        format: null,
        userId: LOCAL_LIBRARY_ID,
      })

      onProgress?.(100)
      const video = storedToVideo(storedVideo)
      setUploadProgress(prev => new Map(prev).set(tempId, {
        videoId: video.id,
        progress: 100,
        status: 'complete'
      }))

      await fetchVideos(false)

      setTimeout(() => {
        setUploadProgress(prev => {
          const next = new Map(prev)
          next.delete(tempId)
          return next
        })
      }, 1500)

      return video
    } catch (error) {
      console.error('Local save failed:', error)
      setUploadProgress(prev => new Map(prev).set(tempId, {
        videoId: tempId,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Save failed'
      }))
      throw error
    }
  }, [fetchVideos])

  const deleteVideo = useCallback(async (videoId: string): Promise<boolean> => {
    await deleteStoredVideo(videoId)
    await fetchVideos(false)
    return true
  }, [fetchVideos])

  const duplicateVideo = useCallback(async (videoId: string): Promise<Video> => {
    const duplicated = await duplicateStoredVideo(videoId)
    await fetchVideos(false)
    return storedToVideo(duplicated)
  }, [fetchVideos])

  const getVideo = useCallback(async (videoId: string): Promise<Video | null> => {
    const video = await getVideoById(videoId)
    return video ? storedToVideo(video) : null
  }, [])

  const refresh = useCallback(() => fetchVideos(true), [fetchVideos])

  const canUpload = useCallback((fileSize: number): boolean => {
    return state.storageInfo.used + fileSize <= state.storageInfo.quota
  }, [state.storageInfo])

  return {
    videos: state.videos,
    isLoading: state.isLoading,
    error: state.error,
    storageInfo: state.storageInfo,
    uploadProgress: Array.from(uploadProgress.values()),
    uploadVideo,
    deleteVideo,
    duplicateVideo,
    getVideo,
    refresh,
    canUpload,
  }
}

export function useCloudStorage() {
  const [storageInfo, setStorageInfo] = useState({
    used: 0,
    quota: DEFAULT_LOCAL_QUOTA,
    videoCount: 0,
    percentUsed: 0
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    getStoredVideos()
      .then((videos) => {
        if (!isMounted) return
        setStorageInfo(calculateStorageInfo(videos.map(storedToVideo)))
      })
      .catch((error) => console.error('Failed to load local storage info:', error))
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  return { storageInfo, isLoading }
}